import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from 'react-tooltip';
import classNames from 'classnames';
import { CaretDownIcon, CaretUpIcon, CheckIcon } from '@seismic/icons';
import Overlay, { useOverlayConfig } from '../../Overlay';
import { invokeFunction, memoize, stopPropagation, useConsistentState } from '../../../utilities';
import { LAYOUTS } from '../../../utilities/layout';
import './style.scss';

// Playback rate options are arraged in ascending order, in this way,
// the first rendered options are on the top.
export const defaultPlaybackRates = [2, 1.75, 1.5, 1.25, 1, 0.75, 0.5, 0.25].sort((a, b) => a - b);

const PlaybackRateText = ({ playbackRate }) => <React.Fragment>{playbackRate.toFixed(2)}&nbsp;x</React.Fragment>;

const ToolbarPlaybackRateSelector = (
  { value, onChange, onToggleMenu, defaultValue = 1, options = defaultPlaybackRates, overlayConfig, layout },
  ref
) => {
  const selectorRef = useRef();
  const menuRef = useRef();
  const [playbackRate, setPlaybackRate] = useConsistentState(value, onChange, defaultValue);
  const [showPlaybackRateMenu, _setShowPlaybackRateMenu] = useState(false);

  const setShowPlaybackRateMenu = useCallback(show => {
    _setShowPlaybackRateMenu(show);
    invokeFunction(onToggleMenu, show);
  }, []);

  const { t } = useTranslation();

  useImperativeHandle(ref, () => ({
    get value() {
      return playbackRate;
    }
  }));

  const onPlaybackRateSelectorClick = useCallback(() => {
    setShowPlaybackRateMenu(true);
  }, []);

  const onPlaybackRateMenuClick = stopPropagation;

  const onPlaybackRateOptionClick = memoize(option => () => {
    setPlaybackRate(option);
    setShowPlaybackRateMenu(false);
    invokeFunction(onChange, option);
  });

  const isCompactLayout = layout === LAYOUTS.SMALL;

  const onBackdropClick = useCallback(e => {
    setShowPlaybackRateMenu(false);
    e.stopPropagation();
  }, []);

  const getOverlayConfig = (isCompactLayout = false) => ({
    containerStyle: { zIndex: 1 },
    hasHandle: !isCompactLayout,
    onBackdropClick,
    origin: selectorRef,
    overlayStyle: isCompactLayout ? { width: '100%', borderRadius: 0 } : null,
    positionStrategy: !isCompactLayout ? 'connected' : 'global',
    positions: !isCompactLayout
      ? {
          originX: 'center',
          originY: 'top',
          overlayX: 'center',
          overlayY: 'bottom',
          offsetX: 0,
          offsetY: -20
        }
      : {
          overlayX: '50%',
          overlayY: '100%',
          offsetX: '-50%',
          offsetY: '-100%'
        }
  });

  const _overlayConfig = useOverlayConfig({}, [overlayConfig, isCompactLayout], (overlayConfig, isCompactLayout) => ({
    ...getOverlayConfig(isCompactLayout),
    ...overlayConfig
  }));

  const optionRefs = [];
  const [active, setActive] = React.useState(0);
  React.useEffect(() => {
    const activeRef = optionRefs[0];
    if (activeRef && activeRef.current) {
      activeRef.current.focus();
      setActive(0);
    } else if (selectorRef.current) {
      selectorRef.current.focus();
    }
  }, [showPlaybackRateMenu]);

  const onSelectorKeyDown = event => {
    switch (event.key) {
      case ' ':
      case 'Spacebar': {
        // reset to first element every time
        // selector menu opens
        setActive(0);
        break;
      }
    }
  };

  const onMenuKeyDown = event => {
    if (showPlaybackRateMenu) {
      event.stopPropagation();
    }

    let newActive = active;
    switch (event.key) {
      case 'ArrowUp': {
        newActive = active - 1;
        newActive = newActive < 0 ? active : newActive;
        break;
      }
      case 'ArrowDown': {
        newActive = active + 1;
        newActive = newActive >= options.length ? active : newActive;
        break;
      }
      case 'Tab': {
        setShowPlaybackRateMenu(false);
        selectorRef.current.focus();
        break;
      }
    }

    const ref = optionRefs[newActive];
    if (ref && ref.current) {
      ref.current.focus();
      setActive(newActive);
    }
  };

  const onOptionKeyDown = (event, speed) => {
    switch (event.key) {
      case ' ':
      case 'Spacebar':
      case 'Enter': {
        onPlaybackRateOptionClick(speed)();
        break;
      }
      default:
        break;
    }
  };

  const menuClasses = classNames('up-playback-rate-menu', {
    'compact-layout': isCompactLayout
  });

  const tooltip = t('Toolbar.PlaybackSpeedSelector.PlaybackSpeed', {
    defaultValue: 'Playback speed'
  });

  return (
    <>
      <button
        aria-expanded={showPlaybackRateMenu}
        aria-haspopup={true}
        aria-label={tooltip}
        className="up-playback-rate-selector"
        onClick={onPlaybackRateSelectorClick}
        onKeyDown={onSelectorKeyDown}
        ref={selectorRef}
        data-tip={tooltip}
        data-for={tooltip}
        datatest="up-playback-rate-selector"
        // a11y: allows component to be reachable via tab
        tabIndex={0}
      >
        {PlaybackRateText({ playbackRate })}
        <span className="up-playback-rate-status">{showPlaybackRateMenu ? <CaretDownIcon /> : <CaretUpIcon />}</span>
        <Tooltip
          id={tooltip}
          className="up-button-tooltip"
          effect="solid"
          event="focusin mouseover"
          eventOff="focusout mouseleave"
          globalEventOff="click"
        />
      </button>
      {showPlaybackRateMenu && (
        <Overlay {..._overlayConfig}>
          <div className={menuClasses} onClick={onPlaybackRateMenuClick} onKeyDown={onMenuKeyDown} ref={menuRef}>
            <div className="up-playback-rate-head">{tooltip}</div>
            {options.map(speed => {
              const selected = speed === playbackRate;
              const classes = classNames(`up-playback-rate-option`, {
                selected
              });
              const ref = React.createRef();
              optionRefs.push(ref);

              const optionAriaLabel = t('Toolbar.PlaybackSpeedSelector.Option', {
                defaultValue: `Select Speed ${speed}`,
                replace: { speed }
              });
              const optionSelectedAriaLabel = t('Toolbar.PlaybackSpeedSelector.OptionSelected', {
                defaultValue: `Currently selected speed ${speed}`,
                replace: { speed }
              });

              return (
                <div
                  aria-label={selected ? optionSelectedAriaLabel : optionAriaLabel}
                  key={speed}
                  className={classes}
                  onClick={onPlaybackRateOptionClick(speed)}
                  onKeyDown={event => onOptionKeyDown(event, speed)}
                  data-value={speed}
                  ref={ref}
                  tabIndex={-1}
                >
                  {PlaybackRateText({ playbackRate: speed })}
                  {selected && (
                    <React.Fragment>
                      <span className="up-playback-rate-tips">
                        (
                        {t('Toolbar.PlaybackSpeedSelector.CurrentSpeed', {
                          defaultValue: 'Current Speed'
                        })}
                        )
                      </span>
                      <span className="up-playback-rate-selected">
                        <CheckIcon />
                      </span>
                    </React.Fragment>
                  )}
                </div>
              );
            })}
          </div>
        </Overlay>
      )}
    </>
  );
};

export default forwardRef(ToolbarPlaybackRateSelector);
